import { useEffect, useMemo, useState } from "react";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Button } from "../ui/Button";
import { CourseMultiSelect } from "./CourseMultiSelect";
import { useToast } from "../ui/ToastContainer";
import { packageService } from "../../services/packageService";
import { courseService } from "../../services/courseService";
import { uploadImage } from "../../services/uploadService";
import { parseApiError } from "../../utils/errorHandler";
import type { Package, PackageFormValues } from "../../types/package";
import { Upload, Loader2, X } from "lucide-react";

interface CourseOption {
  _id: string;
  title: string;
  price?: number;
  level?: string;
}

interface AddEditPackageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pkg?: Package | null;
}

const defaultForm: PackageFormValues = {
  title: "",
  description: "",
  courses: [],
  price: "",
  currency: "USD",
  thumbnail: "",
  validityPeriod: "",
  tagsText: "",
  featuresText: "",
  targetAudienceText: "",
  isPublished: false,
  isActive: true,
};

const parseList = (value?: string) =>
  value
    ? value
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

export function AddEditPackageDialog({
  isOpen,
  onClose,
  onSuccess,
  pkg,
}: AddEditPackageDialogProps) {
  const isEdit = !!pkg?._id;
  const { showError, showSuccess } = useToast();
  const [formValues, setFormValues] = useState<PackageFormValues>(defaultForm);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchCourses();
      if (pkg) {
        setFormValues({
          title: pkg.title,
          description: pkg.description,
          courses: pkg.courses.map((course) => course._id),
          price: String(pkg.price ?? ""),
          currency: pkg.currency || "USD",
          thumbnail: pkg.thumbnail || "",
          validityPeriod:
            pkg.validityPeriod !== null && pkg.validityPeriod !== undefined
              ? String(pkg.validityPeriod)
              : "",
          tagsText: (pkg.tags || []).join(", "),
          featuresText: (pkg.features || []).join("\n"),
          targetAudienceText: (pkg.targetAudience || []).join("\n"),
          isPublished: pkg.isPublished,
          isActive: pkg.isActive,
        });
      } else {
        setFormValues(defaultForm);
      }
      setError(null);
    }
  }, [isOpen, pkg]);

  const fetchCourses = async () => {
    setIsLoadingCourses(true);
    try {
      const response = await courseService.getAllCourses({ limit: 200 });
      setCourses(response.data?.courses || []);
    } catch (err) {
      console.error("Failed to load courses", err);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const selectedCourses = useMemo(() => {
    return courses.filter((course) => formValues.courses.includes(course._id));
  }, [courses, formValues.courses]);

  const originalPrice = useMemo(() => {
    if (!selectedCourses.length) return 0;
    return selectedCourses.reduce(
      (sum, course) => sum + (course.price || 0),
      0
    );
  }, [selectedCourses]);

  const discountPercentage = useMemo(() => {
    const priceNum = Number(formValues.price) || 0;
    if (!originalPrice || priceNum >= originalPrice) return 0;
    return Math.round(((originalPrice - priceNum) / originalPrice) * 100);
  }, [originalPrice, formValues.price]);

  const handleChange = (field: keyof PackageFormValues, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleThumbnailUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);
    try {
      const url = await uploadImage(file, "packages");
      handleChange("thumbnail", url);
      showSuccess("Thumbnail uploaded successfully", "Upload Success");
    } catch (err: any) {
      console.error("Failed to upload image:", err);
      showError(err.message || "Failed to upload image", "Upload Failed");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formValues.courses.length < 2) {
      setError("Select at least two courses.");
      return;
    }

    if (!formValues.title.trim() || !formValues.description.trim()) {
      setError("Title and description are required.");
      return;
    }

    const priceNumber = Number(formValues.price);
    if (Number.isNaN(priceNumber) || priceNumber < 0) {
      setError("Price must be a positive number.");
      return;
    }

    const payload: Record<string, any> = {
      title: formValues.title.trim(),
      description: formValues.description.trim(),
      courses: formValues.courses,
      price: priceNumber,
      currency: formValues.currency,
      thumbnail: formValues.thumbnail || undefined,
      validityPeriod: formValues.validityPeriod
        ? Number(formValues.validityPeriod)
        : undefined,
      tags: parseList(formValues.tagsText),
      features: parseList(formValues.featuresText),
      targetAudience: parseList(formValues.targetAudienceText),
      isPublished: formValues.isPublished,
      isActive: formValues.isActive,
    };

    setIsSubmitting(true);
    try {
      if (isEdit && pkg?._id) {
        await packageService.updatePackage(pkg._id, payload);
        showSuccess("Package updated successfully", "Success");
      } else {
        await packageService.createPackage(payload);
        showSuccess("Package created successfully", "Success");
      }
      onSuccess();
      onClose();
    } catch (err) {
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, isEdit ? "Update Failed" : "Create Failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Package" : "Create Package"}
      className="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Title
            </label>
            <Input
              placeholder="Ultimate Product Management Path"
              value={formValues.title}
              onChange={(e) => handleChange("title", e.target.value)}
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Package Thumbnail
            </label>
            <div className="space-y-4">
              {/* Preview */}
              {formValues.thumbnail && (
                <div className="relative aspect-video w-full max-w-sm overflow-hidden rounded-xl border border-gray-200 bg-gray-50 group">
                  <img
                    src={formValues.thumbnail}
                    alt="Thumbnail Preview"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleChange("thumbnail", "")}
                    className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 shadow-sm"
                    title="Remove Image"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="flex items-center gap-4">
                <input
                  type="file"
                  id="thumbnail-upload"
                  className="hidden"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  disabled={isUploading || isSubmitting}
                />
                <label
                  htmlFor="thumbnail-upload"
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 ${
                    isUploading || isSubmitting
                      ? "pointer-events-none opacity-50"
                      : ""
                  }`}
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                  ) : (
                    <Upload className="h-4 w-4 text-indigo-600" />
                  )}
                  {formValues.thumbnail ? "Change Image" : "Upload Thumbnail"}
                </label>

                {!formValues.thumbnail && (
                  <p className="text-xs text-gray-500">
                    Recommended: 1280x720 (16:9). PNG or JPG, max 5MB.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">
            Description
          </label>
          <Textarea
            rows={3}
            placeholder="Describe the package benefits..."
            value={formValues.description}
            onChange={(e) => handleChange("description", e.target.value)}
            required
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Validity Period (days)
            </label>
            <Input
              type="number"
              min={1}
              placeholder="Optional"
              value={formValues.validityPeriod}
              onChange={(e) => handleChange("validityPeriod", e.target.value)}
            />
          </div>
          <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Pricing summary</p>
            <p>Original total: ${originalPrice.toFixed(2)}</p>
            <p>Discount: {discountPercentage}%</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Price (base currency)
            </label>
            <Input
              type="number"
              min={0}
              value={formValues.price}
              onChange={(e) => handleChange("price", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Currency
            </label>
            <select
              value={formValues.currency}
              onChange={(e) =>
                handleChange("currency", e.target.value as "USD" | "EGP")
              }
              className="h-[43px] w-full rounded-[7px] border border-[#dbdbdb] bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e07828]"
            >
              <option value="USD">USD</option>
              <option value="EGP">EGP</option>
            </select>
          </div>
        </div>

        <CourseMultiSelect
          options={courses}
          selectedIds={formValues.courses}
          onChange={(ids) => handleChange("courses", ids)}
        />
        {isLoadingCourses && (
          <p className="text-xs text-gray-400">Loading course list...</p>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Tags (comma separated)
            </label>
            <Textarea
              rows={2}
              placeholder="bundle, leadership, featured"
              value={formValues.tagsText}
              onChange={(e) => handleChange("tagsText", e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">
              Target Audience (one per line)
            </label>
            <Textarea
              rows={2}
              placeholder="Aspiring PMs"
              value={formValues.targetAudienceText}
              onChange={(e) =>
                handleChange("targetAudienceText", e.target.value)
              }
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">
            Features (one per line)
          </label>
          <Textarea
            rows={3}
            placeholder="All-access to foundational tracks"
            value={formValues.featuresText}
            onChange={(e) => handleChange("featuresText", e.target.value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3">
            <input
              type="checkbox"
              checked={formValues.isPublished}
              onChange={(e) => handleChange("isPublished", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">Publish</p>
              <p className="text-xs text-gray-500">
                Make this package visible to students
              </p>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3">
            <input
              type="checkbox"
              checked={formValues.isActive}
              onChange={(e) => handleChange("isActive", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <p className="text-sm font-semibold text-gray-900">Active</p>
              <p className="text-xs text-gray-500">
                Allow purchases for this package
              </p>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
              ? "Save Changes"
              : "Create Package"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
